"use client";

import { listAdminCoursesApiV1AdminCoursesGet } from "@/src/api/sdk.gen";
import { useState, useEffect } from "react";
import { Link } from "@/src/lib/next-compat";
import {  useNavigate  } from "react-router-dom";
import { ArrowRight, Cpu, BarChart3, Terminal, Sparkles, Upload, Blocks } from "lucide-react";
import { UploadLessonModal } from "@/components/admin/UploadLessonModal";
import { GenerateFromDocModal } from "@/components/admin/lesson-builder/GenerateFromDocModal";
import { MenuDropdown } from "@/components/ui/MenuDropdown";
import type { CourseStats } from "@/components/admin/courses/types";

export default function CoursesPageClient({ initialCourses }: { initialCourses: CourseStats[] }) {
    const navigate = useNavigate();
    const [courses, setCourses] = useState<CourseStats[]>(initialCourses);
    const [loading, setLoading] = useState(false);
    const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
    const [isDocModalOpen, setIsDocModalOpen] = useState(false);

    const fetchCourses = async () => {
        setLoading(true);
        try {
            const { data, error } = await listAdminCoursesApiV1AdminCoursesGet();
            if (!error && data) {
                setCourses(data as unknown as CourseStats[]);
            }
        } catch (error) {
            console.error("Failed to fetch courses:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCourses();
    }, []);

    if (loading) {
        return (
            <div className="space-y-6 animate-pulse">
                <div className="h-8 w-48 bg-slate-200 rounded"></div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="h-56 bg-slate-100 rounded-2xl"></div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            <div className="sticky top-0 z-20 -mx-4 border-b border-slate-200 bg-white px-4 py-3 shadow-sm sm:-mx-6 sm:px-6 md:-mx-8 md:px-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                            <Sparkles className="h-4 w-4" />
                        </div>
                        <h1 className="text-xl md:text-2xl font-bold tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-600">
                            All Courses
                        </h1>
                    </div>
                    <MenuDropdown
                        label="Upload Course"
                        icon={Upload}
                        variant="primary"
                        items={[
                            {
                                label: "AI Generate from doc",
                                icon: Sparkles,
                                onClick: () => setIsDocModalOpen(true),
                            },
                            {
                                label: "Upload md file",
                                icon: Upload,
                                onClick: () => setIsUploadModalOpen(true),
                            },
                            {
                                label: "Upload canonical lessons",
                                icon: Sparkles,
                                onClick: () => navigate("/admin/courses/canonical-lessons"),
                            },
                            {
                                label: "Author lessons",
                                icon: Blocks,
                                onClick: () => navigate("/admin/courses/builder"),
                            },
                        ]}
                    />
                </div>
                <p className="mt-1 text-sm text-slate-500 font-medium leading-tight">
                    Browse all courses and manage uploads from one place.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-5 pt-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {courses.length === 0 ? (
                    <div className="col-span-full text-center py-12 text-slate-500">
                        No courses found.
                    </div>
                ) : (
                    courses.map((course) => {
                        const progress = course.lessonsCount > 0
                            ? Math.round((course.generatedCount / course.lessonsCount) * 100)
                            : 0;

                        let CourseIcon = Cpu;
                        if (course.title.includes("Data")) CourseIcon = BarChart3;
                        else if (course.title.includes("Python")) CourseIcon = Terminal;
                        else if (course.title.includes("GenAI")) CourseIcon = Sparkles;

                        return (
                            <Link
                                key={course.id}
                                to={`/admin/courses/${course.uuid}`}
                                className="block group relative"
                            >
                                <div className="bg-indigo-600 rounded-2xl p-5 text-white relative overflow-hidden transition-all hover:shadow-xl hover:shadow-indigo-500/20 hover:-translate-y-1 h-full">
                                    <div className="absolute top-0 right-0 -mr-16 -mt-16 w-56 h-56 bg-white/10 rounded-full blur-3xl group-hover:bg-white/20 transition-all"></div>

                                    <div className="absolute top-4 right-4 text-indigo-400/30">
                                        <CourseIcon className="h-20 w-20 transform rotate-12" />
                                    </div>

                                    <div className="relative z-10 flex flex-col h-full">
                                        {/* Org name tag */}
                                        {course.orgName && (
                                            <div className="mb-3">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-white/20 text-white/90 border border-white/20 backdrop-blur-sm tracking-wide uppercase">
                                                    {course.orgName}
                                                </span>
                                            </div>
                                        )}
                                        <div className="mb-6 min-h-[3.5rem] flex flex-col items-center text-center px-4">
                                            <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center mb-3 backdrop-blur-sm border border-white/10 shadow-inner">
                                                <CourseIcon className="h-5 w-5 text-white" />
                                            </div>
                                            <h3 className="text-lg font-medium line-clamp-2 uppercase tracking-widest font-montserrat">
                                                {course.title}
                                            </h3>
                                        </div>

                                        <div className="flex items-center gap-6 mb-5 text-center">
                                            <div>
                                                <div className="text-xl font-bold text-white/90">{course.modulesCount}</div>
                                                <div className="text-[10px] text-indigo-100 mt-0.5 uppercase tracking-wide font-medium">Modules</div>
                                            </div>
                                            <div>
                                                <div className="text-xl font-bold text-white/90">{course.lessonsCount}</div>
                                                <div className="text-[10px] text-indigo-100 mt-0.5 uppercase tracking-wide font-medium">Lessons</div>
                                            </div>
                                            <div>
                                                <div className="text-xl font-bold text-emerald-300">{course.generatedCount}</div>
                                                <div className="text-[10px] text-indigo-100 mt-0.5 uppercase tracking-wide font-medium">Generated</div>
                                            </div>
                                        </div>

                                        <div className="mb-2">
                                            <div className="h-1.5 bg-indigo-800/50 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-300 transition-all duration-500"
                                                    style={{ width: `${progress}%` }}
                                                />
                                            </div>
                                            <div className="text-[10px] text-indigo-100 mt-1.5">
                                                {progress}% complete
                                            </div>
                                        </div>

                                        <div className="mt-auto pt-3 border-t border-indigo-500/30">
                                            <div className="flex items-center justify-between text-xs font-medium hover:text-indigo-300 transition-colors">
                                                View Details
                                                <ArrowRight className="h-3.5 w-3.5" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        );
                    })
                )}
            </div>

            <UploadLessonModal
                isOpen={isUploadModalOpen}
                onClose={() => setIsUploadModalOpen(false)}
                onSuccess={() => {
                    fetchCourses();
                    setIsUploadModalOpen(false);
                }}
            />

            <GenerateFromDocModal
                isOpen={isDocModalOpen}
                onClose={() => setIsDocModalOpen(false)}
                onSuccess={(newCourseId) => {
                    navigate(`/admin/courses/builder?course=${newCourseId}`);
                }}
            />
        </div>
    );
}
